import { Eye } from "lucide-react";

import { useAccount } from "@/contexts/account.context";
import { useAuth } from "@/contexts/auth.context";

/**
 * A faixa que diz a quem navega por escopo cedido o que o acesso dele é.
 *
 * Ela existe porque o modo somente leitura é **silencioso** por natureza: quem entra por
 * essa trilha vê os mesmos painéis que a agência vê da loja, com os mesmos números, e sem um
 * aviso permanente a primeira pista de que não pode mudar nada seria um `403` no meio de um
 * formulário preenchido.
 *
 * São **duas** pessoas, e o texto muda para cada uma: o funcionário da agência, que alcança
 * as lojas dos times dele e a quem interessa saber de qual time cada loja vem, e a
 * credencial cedida ao cliente (D-060), que não participa de time nenhum — dizer-lhe "as
 * lojas do seu time" seria falar de uma coisa que ela não tem.
 *
 * Fica no topo do conteúdo, na mesma pilha dos avisos de retenção e de armazenamento, e não
 * fecha: não é uma notificação, é a descrição do acesso — some quando o acesso mudar (o
 * time desativado, o funcionário tirado dele ou a loja retirada da credencial deixam de
 * aparecer na requisição seguinte).
 *
 * A barreira de verdade está no servidor (`TenantGuardService`, no `pde-api-server`), que
 * recusa todo método que não seja de leitura para quem é `viewer` na loja. Esta faixa e os
 * botões escondidos pelo app são cortesia com quem usa, nunca a proteção.
 *
 * <M-248> Ela deixou de aparecer para **todo** mundo que navega por escopo cedido: quem foi
 * promovido a `editor` escreve na loja que acompanha (<D-061>), e dizer-lhe que nada pode
 * ser alterado era a interface contradizendo o servidor. A faixa segue a loja **aberta**,
 * porque é a loja que a barreira avalia — trocar de loja pode trocar a resposta.
 */
export const TeamScopeBanner = () => {
  const { teamScope, canManageTeams } = useAuth();
  const { isReadOnly, acessoResolvido, selectedAccount } = useAccount();

  // A faixa não pisca: enquanto a loja ativa não foi escolhida, `isReadOnly` é o palpite
  // fechado do primeiro instante, e mostrá-la ali afirmaria "somente leitura" a um `editor`
  // por um quadro. Achado de code review.
  if (!acessoResolvido || !isReadOnly) return null;

  const times = teamScope?.teams ?? [];
  const nomes = times.map((time) => time.name).join(", ");

  /**
   * O texto fala da loja **aberta**, e não do escopo.
   *
   * <M-248> Antes as duas coisas eram a mesma: o alcance cedido recusava escrita em todas as
   * lojas, e "as lojas do time são somente leitura" era verdade sobre o conjunto. Agora o
   * nível é por vínculo, e a mesma pessoa pode ser `viewer` numa loja e `editor` na outra —
   * afirmar o conjunto seria contradizer os botões que ela vê ao trocar de loja. Achado de
   * code review.
   */
  const loja = selectedAccount?.name;

  return (
    <div className="border-b border-sky-200 bg-sky-50 px-4 py-3 sm:px-6 dark:border-sky-900 dark:bg-sky-950">
      <div className="mx-auto flex max-w-7xl items-center gap-3">
        <Eye className="h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
        <p className="text-sm text-sky-900 dark:text-sky-200">
          <span className="font-medium">
            {loja
              ? `${loja} é somente leitura para você.`
              : "Esta loja é somente leitura para você."}
          </span>{" "}
          {nomes
            ? `Você acompanha as lojas ${times.length > 1 ? "dos times" : "do time"} ${nomes}.`
            : "Você acompanha as lojas que a agência liberou para você."}{" "}
          {/* O funcionário com papel administrativo é membro **e** gere times: dizer-lhe
              que "nada aqui pode ser alterado" seria falso, e ele veria a frase justamente
              na tela em que edita a composição. O que é somente leitura, para os dois, são
              os dados desta loja — e é só isso que a faixa promete. */}
          {canManageTeams
            ? "Os dados dela não podem ser alterados por este acesso."
            : "Nenhum dado dela pode ser alterado por você."}
        </p>
      </div>
    </div>
  );
};

export default TeamScopeBanner;
